import { AxiosResponse } from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import RequisitionType from "../../../Services/Requisition/RequisitionTypeService";
import DatePickerDuplicate from "../../../Shared/Common/DatePicker/DatePickerDuplicate";
import { reactSelectSMStyle, styles } from "../../../Utils/Common";
import BreadCrumbs from "../../../Utils/Common/Breadcrumb";
import { convertLabDateRangeToUtc } from "../../../Utils/Common/LabTimeZone";
import { validateFields } from "../Toxicology/CommonFunctions";
import TestsSelection, {
  EMPTY_TESTS_LOOKUP,
} from "./TestsSelection";
import {
  GetBloodTestsLookup,
  GetTOXAndBloodInstrumentsLookup,
  GetToxPanelTypes,
  GetTOXSpecimenTypesLookup,
  LeveyJenningDownloadReport,
  QCLevelsLookup,
} from "Services/ToxLeveyJennings";
import Radio from "Shared/Common/Input/Radio";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import useLang from "Shared/hooks/useLanguage";
import { StringRecord } from "Shared/Type";

interface ToxLeveyJenningsValues {
  fromDate: string;
  toDate: string;
  reqTypeId: number;
  instrumentId: number;
  panelTypeId: number;
  specimenTypeId: number;
  qcLevelIds: number[];
  testConfigIds: number[];
}

const TOX_REQ_TYPE_ID = 3;
const CONFIRMATION_PANEL_TYPE_ID = 2;
const SCREENING_PANEL_TYPE_ID = 3;
const CONFIRMATION_INSTRUMENT_ID = 0;
const UNSELECTED_INSTRUMENT_ID = -1;

const CONFIRMATION_INSTRUMENT_OPTIONS = [
  { label: "LCMS1", value: CONFIRMATION_INSTRUMENT_ID },
];

const initialToxLeveyJenning: ToxLeveyJenningsValues = {
  fromDate: "",
  toDate: "",
  reqTypeId: 0,
  instrumentId: UNSELECTED_INSTRUMENT_ID,
  panelTypeId: 0,
  specimenTypeId: 0,
  qcLevelIds: [],
  testConfigIds: [],
};

const ToxLeveyJenning = () => {
  const { t } = useLang();

  const queryDisplayTagNames: StringRecord = {
    panelTypeId: "Test Type",
    instrumentId: "Instrument",
    specimenTypeId: "Specimen Type",
    fromDate: "Start Date",
    toDate: "End Date",
    testConfigIds: "Selected Analytes",
  };

  const [dropDownValues, setDropDownValues] = useState<any>({
    TestsLookup: [],
    ToxPanelLookup: [],
    QCLevelsLookup: [],
    InstrumentLookup: [],
    SpecimentTypesLookup: [],
  });
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [toxLeveyJenningsValues, setToxLeveyJenningsValues] =
    useState<ToxLeveyJenningsValues>(initialToxLeveyJenning);
  const [filterDate, setFilterDate] = useState<any>();
  const labTimeZone = useSelector(
    (state: any) => state?.Reducer?.selectedTenantInfo?.labTimeZone
  );

  const { panelTypeId, instrumentId, specimenTypeId } = toxLeveyJenningsValues;
  const hasTestType = !!panelTypeId;
  const isConfirmation = panelTypeId === CONFIRMATION_PANEL_TYPE_ID;
  const isScreening = panelTypeId === SCREENING_PANEL_TYPE_ID;
  const hasInstrument = isConfirmation
    ? instrumentId === CONFIRMATION_INSTRUMENT_ID
    : !!instrumentId;
  const hasSpecimenType = !!specimenTypeId;
  const instrumentOptions = isConfirmation
    ? CONFIRMATION_INSTRUMENT_OPTIONS
    : dropDownValues.InstrumentLookup;
  const isInstrumentEnabled = hasTestType;
  const isSpecimenTypeEnabled = isConfirmation && hasInstrument;
  const isQcLevelEnabled = hasTestType && (isScreening ? hasInstrument : hasSpecimenType);
  const isDateRangeEnabled = hasInstrument;
  const isAnalytePanelEnabled = isQcLevelEnabled;

  const dateRange = filterDate ? filterDate : "";
  const [startDate, endDate] = dateRange.split("to");
  const start = moment(startDate).format("MM/DD/YYYY");
  const end = moment(endDate).format("MM/DD/YYYY");

  const GetToxPanelTypesLookup = () => {
    GetToxPanelTypes()
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          ToxPanelLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const GetSpecimenTypesLookup = () => {
    GetTOXSpecimenTypesLookup()
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          SpecimentTypesLookup: res?.data ?? [],
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const GetInstrumentsLookup = (reqTypeId: number, panelTypeId: number) => {
    GetTOXAndBloodInstrumentsLookup(reqTypeId, panelTypeId)
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          InstrumentLookup: res?.data ?? [],
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const QCLevelsLookupApi = (
    reqTypeId: number,
    instrumentId: number,
    panelTypeId: number,
    specimenTypeId: number
  ) => {
    QCLevelsLookup(reqTypeId, instrumentId, panelTypeId, specimenTypeId)
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          QCLevelsLookup: res?.data ?? [],
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const GetConfirmationTestsLookupApi = (
    specimenTypeId: number,
    selectedInstrumentId: number
  ) => {
    GetBloodTestsLookup({
      specimenTypeId,
      panelTypeId: CONFIRMATION_PANEL_TYPE_ID,
      reqTypeId: TOX_REQ_TYPE_ID,
      instrumentId: selectedInstrumentId,
      qcLevelIds: [],
    })
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          TestsLookup: res?.data ?? [],
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const GetScreeningTestsLookupApi = (instrumentId: number) => {
    GetBloodTestsLookup({
      specimenTypeId: 0,
      panelTypeId: SCREENING_PANEL_TYPE_ID,
      reqTypeId: TOX_REQ_TYPE_ID,
      instrumentId,
      qcLevelIds: [],
    })
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          TestsLookup: res?.data ?? [],
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const resetDependentDropdowns = () => ({
    TestsLookup: [],
    InstrumentLookup: [],
    QCLevelsLookup: [],
    SpecimentTypesLookup: [],
  });

  const handleTestTypeChange = (event: any) => {
    const nextPanelTypeId = Number(event?.target?.value ?? 0);

    setFilterDate(undefined);
    setToxLeveyJenningsValues({
      ...initialToxLeveyJenning,
      panelTypeId: nextPanelTypeId,
      instrumentId:
        nextPanelTypeId === CONFIRMATION_PANEL_TYPE_ID
          ? UNSELECTED_INSTRUMENT_ID
          : 0,
    });
    setDropDownValues((prev: any) => ({
      ...prev,
      ...resetDependentDropdowns(),
      InstrumentLookup:
        nextPanelTypeId === CONFIRMATION_PANEL_TYPE_ID
          ? CONFIRMATION_INSTRUMENT_OPTIONS
          : [],
    }));

    if (nextPanelTypeId === SCREENING_PANEL_TYPE_ID) {
      GetInstrumentsLookup(TOX_REQ_TYPE_ID, SCREENING_PANEL_TYPE_ID);
    }
  };

  const handleInstrumentChange = (event: any) => {
    const nextInstrumentId = event?.value ?? 0;

    setFilterDate(undefined);

    if (isConfirmation) {
      setToxLeveyJenningsValues((prev) => ({
        ...prev,
        instrumentId: nextInstrumentId,
        specimenTypeId: 0,
        qcLevelIds: [],
        testConfigIds: [],
      }));
      setDropDownValues((prev: any) => ({
        ...prev,
        SpecimentTypesLookup: [],
        QCLevelsLookup: [],
        TestsLookup: [],
      }));

      if (nextInstrumentId === CONFIRMATION_INSTRUMENT_ID) {
        GetSpecimenTypesLookup();
      }
      return;
    }

    if (isScreening) {
      setToxLeveyJenningsValues((prev) => ({
        ...prev,
        instrumentId: nextInstrumentId,
        qcLevelIds: [],
        testConfigIds: [],
      }));
      setDropDownValues((prev: any) => ({
        ...prev,
        QCLevelsLookup: [],
        TestsLookup: [],
      }));

      if (nextInstrumentId) {
        QCLevelsLookupApi(
          TOX_REQ_TYPE_ID,
          nextInstrumentId,
          SCREENING_PANEL_TYPE_ID,
          0
        );
        GetScreeningTestsLookupApi(nextInstrumentId);
      }
    }
  };

  const handleSpecimenTypeChange = (event: any) => {
    const nextSpecimenTypeId = event?.value ?? 0;

    setToxLeveyJenningsValues((prev) => ({
      ...prev,
      specimenTypeId: nextSpecimenTypeId,
      qcLevelIds: [],
      testConfigIds: [],
    }));
    setDropDownValues((prev: any) => ({
      ...prev,
      QCLevelsLookup: [],
      TestsLookup: [],
    }));

    if (nextSpecimenTypeId) {
      QCLevelsLookupApi(
        TOX_REQ_TYPE_ID,
        CONFIRMATION_INSTRUMENT_ID,
        CONFIRMATION_PANEL_TYPE_ID,
        nextSpecimenTypeId
      );
      GetConfirmationTestsLookupApi(
        nextSpecimenTypeId,
        CONFIRMATION_INSTRUMENT_ID
      );
    }
  };

  const handleChange = (
    event: any,
    name?: string,
    isMulti: boolean = false
  ) => {
    if (isMulti) {
      setToxLeveyJenningsValues((prev) => ({
        ...prev,
        [name as string]: event?.map((item: any) => item.value) ?? [],
      }));
    }
  };

  const DownloadReport = async () => {
    const ObjToValidate: Record<string, string | number | boolean> = {
      panelTypeId: toxLeveyJenningsValues.panelTypeId,
      instrumentId: hasInstrument,
      fromDate: start === "Invalid date" ? "" : start,
      toDate: end === "Invalid date" ? "" : end,
      testConfigIds: toxLeveyJenningsValues.testConfigIds.length,
    };

    if (isConfirmation) {
      ObjToValidate.specimenTypeId = toxLeveyJenningsValues.specimenTypeId;
    }

    const { isValid, invalidField } = validateFields(ObjToValidate);
    if (isValid) {
      setIsDisabled(true);
      // The picked days are lab days; the API receives them as UTC: dates only, or
      // the exact window once SEND_UTC_TIME_WITH_DATE_RANGE_FILTERS is turned on.
      const utcRange = filterDate
        ? convertLabDateRangeToUtc(String(filterDate), labTimeZone)
        : null;
      const [fromDate, toDate] = utcRange ? utcRange.split(" to ") : [start, end];
      const payload = {
        reqTypeId: TOX_REQ_TYPE_ID,
        panelTypeId: toxLeveyJenningsValues.panelTypeId,
        specimenTypeId: isScreening ? 0 : toxLeveyJenningsValues.specimenTypeId,
        instrumentId: toxLeveyJenningsValues.instrumentId,
        qcLevelIds: toxLeveyJenningsValues.qcLevelIds,
        fromDate,
        toDate,
        testConfigIds: toxLeveyJenningsValues.testConfigIds,
      };
      try {
        await LeveyJenningDownloadReport(payload).then((res: AxiosResponse) => {
          if (res?.data?.statusCode === 200) {
            toast.success(
              res?.data?.message || "Report generated successfully"
            );
            RequisitionType.ShowBlob(res?.data?.data).then((res: any) => {
              window.open(res?.data?.Data.replace("}", ""), "_blank");
            });
          } else if (res?.data?.statusCode === 400) {
            toast.error(res?.data?.message || "Error generating report");
          }
        });
      } catch (error) {
        console.trace(error);
      } finally {
        setIsDisabled(false);
      }
    } else {
      const errorMessage = invalidField
        ? t(`Please Fill ${queryDisplayTagNames[invalidField] ?? ""} field`)
        : t("Invalid field found");
      toast.error(t(errorMessage));
    }
  };

  useEffect(() => {
    GetToxPanelTypesLookup();
  }, []);

  return (
    <>
      <div className="d-flex flex-column flex-column-fluid py-2 py-lg-3">
        <div className="app-toolbar py-2 py-lg-3">
          <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
            <BreadCrumbs />
          </div>
        </div>
        <div className="app-toolbar py-2 py-lg-3">
          <div className="d-flex flex-column flex-column-fluid">
            <div className="app-content flex-column-fluid">
              <div className="app-container container-fluid">
                <div className="bg-white p-6">
                  <h3 className="mb-5">{t("Report")}</h3>
                  <div className="mb-3">
                    <Radio
                      label={t("Test Type")}
                      name="panelTypeId"
                      onChange={handleTestTypeChange}
                      choices={dropDownValues.ToxPanelLookup}
                      checked={toxLeveyJenningsValues.panelTypeId}
                    />
                  </div>
                  {hasTestType && (
                    <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
                      <div className="d-flex gap-2 responsive-flexed-actions">
                        <div className="d-flex gap-2 gap-lg-3 justify-content-center justify-content-sm-start responsive-flexed-actions flex-wrap align-items-end">
                          <div className="mt-0">
                            <label className="required mb-2 fw-500">
                              {t("Instrument")}
                            </label>
                            <Select
                              menuPortalTarget={document.body}
                              theme={(theme) => styles(theme)}
                              placeholder="Instruments"
                              options={instrumentOptions}
                              styles={reactSelectSMStyle}
                              isDisabled={!isInstrumentEnabled}
                              onChange={handleInstrumentChange}
                              value={instrumentOptions?.filter(
                                (option: any) =>
                                  option.value ===
                                  toxLeveyJenningsValues.instrumentId
                              )}
                            />
                          </div>
                          {isConfirmation && (
                            <div className="mt-0">
                              <label className="required mb-2 fw-500">
                                {t("Specimen Type")}
                              </label>
                              <Select
                                menuPortalTarget={document.body}
                                theme={(theme) => styles(theme)}
                                placeholder="Select Specimen Type"
                                options={dropDownValues.SpecimentTypesLookup}
                                styles={reactSelectSMStyle}
                                isDisabled={!isSpecimenTypeEnabled}
                                onChange={handleSpecimenTypeChange}
                                value={dropDownValues.SpecimentTypesLookup?.filter(
                                  (option: any) =>
                                    option.value ===
                                    toxLeveyJenningsValues.specimenTypeId
                                )}
                              />
                            </div>
                          )}
                          <div className="mt-0" style={{ maxWidth: "30%" }}>
                            <label className="mb-2 fw-500">
                              {isConfirmation
                                ? t("QC Level Code")
                                : t("QC Level")}
                            </label>
                            <Select
                              menuPortalTarget={document.body}
                              theme={(theme) => styles(theme)}
                              placeholder="Select QC Level"
                              options={dropDownValues.QCLevelsLookup}
                              isMulti
                              isDisabled={!isQcLevelEnabled}
                              styles={reactSelectSMStyle}
                              onChange={(event: any) =>
                                handleChange(event, "qcLevelIds", true)
                              }
                              value={dropDownValues.QCLevelsLookup.filter(
                                (option: any) =>
                                  toxLeveyJenningsValues.qcLevelIds.includes(
                                    option.value
                                  )
                              )}
                            />
                          </div>
                          <div className="align-items-center">
                            <label className="required mb-2 fw-500">
                              {t("Date Range")}
                            </label>
                            <div>
                              <DatePickerDuplicate
                                setFilterData={setFilterDate}
                                filterData={filterDate}
                                disabled={!isDateRangeEnabled}
                              />
                            </div>
                          </div>
                          <PermissionComponent
                            moduleName="Tox LIS"
                            pageName="Levey jenning Report"
                            permissionIdentifier="View"
                          >
                            {({
                              pageName,
                              moduleName,
                              permissionIdentifier,
                            }) => (
                              <button
                                className="btn btn-icon btn-sm fw-bold btn-warning"
                                onClick={DownloadReport}
                                disabled={isDisabled}
                                title={getPermissionDisplayName(
                                  pageName,
                                  moduleName,
                                  permissionIdentifier,
                                  t("View")
                                )}
                              >
                                <i className="bi bi-cloud-download"></i>
                              </button>
                            )}
                          </PermissionComponent>
                        </div>
                      </div>
                    </div>
                  )}
                  <hr />
                  <TestsSelection
                    testsLookup={
                      isAnalytePanelEnabled
                        ? dropDownValues.TestsLookup
                        : EMPTY_TESTS_LOOKUP
                    }
                    setLeveyJenningsValues={setToxLeveyJenningsValues}
                    allTestsHeader={
                      isScreening
                        ? t("All Drug Classes")
                        : t("All Analytes")
                    }
                    selectedTestsHeader={
                      isScreening
                        ? t("Selected Drug Classes")
                        : t("Selected Analytes")
                    }
                    disabled={!isAnalytePanelEnabled}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ToxLeveyJenning;
